import { useCallback, useState } from 'react';
import { createApiErrorHandler, useApiClient } from '@m43/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useToast } from '@cad-arc';
import {
    SubjectStopResource,
    SubjectStopForm,
    SubjectStopBootstrapResponse,
    SubjectStopOptions,
} from '@mark43/cad-api';

import { useDrawerControls } from '../drawers/components/drawer-context-provider';
import { useCurrentUser } from '../current-user/current-user';
import { getSubjectDataFormDefaultValues } from './helpers';
import { EMPTY_SUBJECT, SUBJECT_DATA_DEFAULT_OPTIONS } from './constants';
import { useEventSubjectDataFormResolver } from './use-event-subject-data-form-resolver';
import { errorOccurredWhile } from '../common/helpers/error-occurred-while';

export function useEventSubjectDataState() {
    const apiClient = useApiClient();
    const toast = useToast();
    const drawerControls = useDrawerControls();
    const currentUser = useCurrentUser();
    const eventSubjectDataFormResolver = useEventSubjectDataFormResolver();
    const [subjectStopOptions, setSubjectStopOptions] = useState<SubjectStopOptions>(
        SUBJECT_DATA_DEFAULT_OPTIONS
    );
    const form = useForm<SubjectStopForm>({
        mode: 'onBlur',
        resolver: eventSubjectDataFormResolver,
    });

    const prefillSubjectDataForm = useCallback(
        (queryResponse: SubjectStopBootstrapResponse) => {
            const defaultValues = getSubjectDataFormDefaultValues({
                currentUserId: currentUser.userId,
                subjectDataQueryResponse: queryResponse,
            });

            const {
                subjectDataForms,
                ...remainingFormFieldsFromQuery
            } = queryResponse.subjectStopForm;

            const prefillValues = {
                ...defaultValues,
                ...remainingFormFieldsFromQuery,
            };

            for (const [key, prefillValue] of Object.entries(prefillValues)) {
                if (prefillValue) {
                    form.setValue(key as keyof SubjectStopForm, prefillValue);
                }
            }

            if (subjectDataForms?.length) {
                form.setValue('subjectDataForms', subjectDataForms);
            } else {
                form.setValue('subjectDataForms', [EMPTY_SUBJECT]);
            }
        },
        [form, currentUser]
    );

    const query = useQuery({
        queryKey: ['event-subject-data-view'],
        queryFn() {
            return apiClient
                .request<SubjectStopResource.BootstrapSubjectStop>({
                    method: 'POST',
                    url: `subject_stop/bootstrap/`,
                    data: {},
                })
                .then((data) => {
                    const response = data?.data?.data;

                    if (!response) {
                        return;
                    }

                    prefillSubjectDataForm(response);
                    setSubjectStopOptions(response.subjectStopOptions);
                    return response;
                });
        },
        onError: createApiErrorHandler(() => {
            drawerControls.close();
            toast({ status: 'error', description: errorOccurredWhile('retrieving subject data') });
        }),
    });

    const saveMutation = useMutation(
        (subjectStopForm: SubjectStopForm) =>
            apiClient.request<SubjectStopResource.SaveSubjectStop>({
                method: 'POST',
                url: `subject_stop/`,
                data: { subjectStopForm },
                timeout: 10000,
            }),
        {
            onSuccess() {
                drawerControls.close();
                const agencyEventNumber = form.getValues().agencyEventNumber;

                toast({
                    status: 'default',
                    description: `Subject data submitted for Event #${agencyEventNumber}`,
                });
            },
            onError: createApiErrorHandler(() => {
                drawerControls.close();
                toast({ status: 'error', description: errorOccurredWhile('saving subject data') });
            }),
        }
    );

    const onSubmit = useCallback(
        (subjectStopForm: SubjectStopForm) => {
            if (saveMutation.isLoading) {
                return;
            }

            return saveMutation.mutateAsync(subjectStopForm);
        },
        [saveMutation]
    );

    return {
        query,
        form,
        saveMutation,
        onSubmit,
        subjectStopOptions,
    };
}
